import React, { useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { RideWiseRow, DriverRow } from "./types";
import { fmtMoney, fmtNum, safeArr } from "./helpers";

interface Props {
  rw: RideWiseRow[];
  driversById: Map<string, DriverRow>;
  loading: boolean;
}

interface Agg {
  driver_number: string;
  driver_name: string;
  status: "Active" | "Inactive" | "Historical / Not in driver list";
  jobs: number;
  passengers: number;
  fare_total: number;
  subsidy: number;
  km: number;
  hoist: number;
  days: Set<string>;
  lastTrip: string;
}

const DriversTab: React.FC<Props> = ({ rw, driversById, loading }) => {
  const [q, setQ] = useState("");

  const rows = useMemo(() => {
    const map = new Map<string, Agg>();
    for (const r of safeArr(rw)) {
      const key = String(r.driver_number || "—");
      const drv = driversById.get(key);
      const status: Agg["status"] = !drv
        ? "Historical / Not in driver list"
        : drv.is_active
        ? "Active"
        : "Inactive";
      const name =
        drv?.driver_name ||
        [drv?.driver_firstname, drv?.driver_lastname].filter(Boolean).join(" ") ||
        `Driver ${key}`;
      const cur =
        map.get(key) ||
        ({
          driver_number: key,
          driver_name: name,
          status,
          jobs: 0, passengers: 0, fare_total: 0, subsidy: 0, km: 0, hoist: 0,
          days: new Set<string>(), lastTrip: "",
        } as Agg);
      cur.jobs += 1;
      cur.passengers += Number(r.number_of_passengers || 0);
      cur.fare_total += Number(r.fare_total || 0);
      cur.subsidy += Number(r.concession_amount || 0);
      cur.km += Number(r.kilometres || 0);
      if (r.hoist_deployed) cur.hoist += 1;
      if (r.trip_date) {
        cur.days.add(r.trip_date);
        if (r.trip_date > cur.lastTrip) cur.lastTrip = r.trip_date;
      }
      map.set(key, cur);
    }
    return Array.from(map.values()).sort((a, b) => b.jobs - a.jobs);
  }, [rw, driversById]);

  const filtered = rows.filter((r) =>
    !q.trim() ||
    r.driver_number.toLowerCase().includes(q.toLowerCase()) ||
    r.driver_name.toLowerCase().includes(q.toLowerCase())
  );

  if (loading) {
    return (
      <Card className="bg-card border border-border">
        <CardContent className="py-12 text-center text-muted-foreground">Loading drivers…</CardContent>
      </Card>
    );
  }

  if (!rows.length) {
    return (
      <Card className="bg-card border border-border">
        <CardContent className="py-12 text-center text-muted-foreground">No driver data in period.</CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-card border border-border">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-base">Drivers ({fmtNum(rows.length)})</CardTitle>
        <Input
          placeholder="Search driver…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="dark-input max-w-xs"
        />
      </CardHeader>
      <CardContent className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="text-muted-foreground border-b border-border">
            <tr>
              <th className="text-left p-2">Driver</th>
              <th className="text-left p-2">#</th>
              <th className="text-left p-2">Status</th>
              <th className="text-right p-2">Jobs</th>
              <th className="text-right p-2">Pax</th>
              <th className="text-right p-2">Fare</th>
              <th className="text-right p-2">Subsidy</th>
              <th className="text-right p-2">KM</th>
              <th className="text-right p-2">Hoist</th>
              <th className="text-right p-2">Days</th>
              <th className="text-right p-2">Jobs/Day</th>
              <th className="text-left p-2">Last Trip</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => {
              const days = r.days.size || 1;
              const jpd = r.jobs / days;
              return (
                <tr key={r.driver_number} className="border-b border-border/50">
                  <td className="p-2">{r.driver_name}</td>
                  <td className="p-2 font-mono">{r.driver_number}</td>
                  <td className="p-2">
                    <Badge
                      variant={r.status === "Active" ? "default" : "secondary"}
                      className={r.status === "Active" ? "bg-green-700 text-white" : ""}
                    >
                      {r.status}
                    </Badge>
                  </td>
                  <td className="p-2 text-right">{fmtNum(r.jobs)}</td>
                  <td className="p-2 text-right">{fmtNum(r.passengers)}</td>
                  <td className="p-2 text-right">{fmtMoney(r.fare_total)}</td>
                  <td className="p-2 text-right">{fmtMoney(r.subsidy)}</td>
                  <td className="p-2 text-right">{fmtNum(r.km, 1)}</td>
                  <td className="p-2 text-right">{fmtNum(r.hoist)}</td>
                  <td className="p-2 text-right">{fmtNum(days)}</td>
                  <td className="p-2 text-right">{fmtNum(jpd, 1)}</td>
                  <td className="p-2">{r.lastTrip || "—"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
};

export default DriversTab;
